/**
 * The profession vocabulary for `users.practitioner_type` (CLA-1141).
 *
 * `Claro.Accounts.PractitionerType` in claro-elixir is the authority and now
 * SERVES this list at `GET /api/v1/practitioner-types` (CLA-1148). The array
 * below is a FALLBACK, not the source: it exists so a label renders before the
 * fetch lands and on a pre-auth surface, and it reproduces the backend seed
 * exactly so both sides degrade identically.
 *
 * `hydrate/1` replaces it once the served list arrives, and the result is
 * persisted to localStorage so the next load starts current. Same shape as
 * `lib/jurisdiction.ts` (CLA-859): a module helper rather than React state,
 * because this vocabulary changes when someone edits the backend module and
 * deploys — not while a screen is open.
 *
 * The consequence worth knowing: a profession added on the server appears
 * without a client release. A profession that exists ONLY here cannot be
 * saved — the backend rejects an unknown slug with a 400.
 *
 * ## This is NOT the registration question
 *
 * `practitioner_type` used to hold two questions at once. Onboarding, public
 * signup and the owner invite all collect an AHPRA *registration status*
 * (`registered` / `non_registered` / `admin_only`) and wrote it into the
 * column the note prompt reads as a *profession* — so a self-serve user
 * rendered `Role: registered` into their prompt and silently failed every
 * role gate. Those live in `registration_status` now; see
 * `REGISTRATION_STATUSES` below, and `practitionerLabels()` in
 * `@/lib/jurisdiction` for the AU/US wording of that question.
 *
 * ## Registration marker
 *
 * `registration` says whether the profession is registered under the National
 * Law. It exists so a label never implies registration a person does not
 * hold: the Australian aesthetics workforce (dermal therapists, dermal
 * clinicians, laser and beauty therapists) holds real qualifications but no
 * national registration and no protected title. Until this vocabulary existed
 * the nearest value was `allied_health`, which both misdescribes them and
 * implies registration.
 *
 * `other` is `unknown`, not `none` — we assert neither way about a role we
 * cannot identify.
 */

export type Registration = 'ahpra' | 'none' | 'unknown'

export interface PractitionerTypeOption {
  value: string
  label: string
  registration: Registration
}

/**
 * The built-in fallback. Mirrors the backend seed; superseded by `hydrate/1`.
 * Not exported as the vocabulary — read `practitionerTypes()` instead, or a
 * caller silently pins itself to whatever this file shipped with.
 */
const SEED_PRACTITIONER_TYPES: PractitionerTypeOption[] = [
  { value: 'doctor', label: 'Doctor (medical practitioner)', registration: 'ahpra' },
  { value: 'nurse_practitioner', label: 'Nurse Practitioner', registration: 'ahpra' },
  { value: 'registered_nurse', label: 'Registered Nurse', registration: 'ahpra' },
  { value: 'enrolled_nurse', label: 'Enrolled Nurse', registration: 'ahpra' },
  { value: 'clinical_nurse_specialist', label: 'Clinical Nurse Specialist', registration: 'ahpra' },
  { value: 'clinical_nurse_consultant', label: 'Clinical Nurse Consultant', registration: 'ahpra' },
  { value: 'nurse', label: 'Nurse', registration: 'ahpra' },
  { value: 'midwife', label: 'Midwife', registration: 'ahpra' },
  { value: 'pharmacist', label: 'Pharmacist', registration: 'ahpra' },
  { value: 'dentist', label: 'Dentist', registration: 'ahpra' },
  { value: 'physiotherapist', label: 'Physiotherapist', registration: 'ahpra' },
  { value: 'psychologist', label: 'Psychologist', registration: 'ahpra' },
  { value: 'paramedic', label: 'Paramedic', registration: 'ahpra' },
  { value: 'allied_health', label: 'Allied Health Professional', registration: 'ahpra' },
  { value: 'dermal_therapist', label: 'Dermal Therapist', registration: 'none' },
  { value: 'dermal_clinician', label: 'Dermal Clinician', registration: 'none' },
  { value: 'laser_therapist', label: 'Laser Therapist', registration: 'none' },
  { value: 'beauty_therapist', label: 'Beauty Therapist', registration: 'none' },
  { value: 'aesthetician', label: 'Aesthetician', registration: 'none' },
  { value: 'other', label: 'Other health professional', registration: 'unknown' },
]

const STORAGE_KEY = 'claro:practitioner-types'
/**
 * When the stored vocabulary was last fetched, so a cold boot can skip the
 * refetch (CLA-1170). Separate key rather than a wrapper object so an existing
 * stored array from before this change still parses — it simply reads as
 * "never fetched" and refetches once, which is the safe direction.
 */
const STORAGE_AT_KEY = 'claro:practitioner-types:at'

/**
 * How long a stored copy is trusted without re-asking.
 *
 * React Query's `staleTime` cannot help here: a cold page load starts with an
 * empty cache, so every boot refetched a list of professions that changes
 * approximately never — one of only two requests in the first wave, racing
 * `/auth/me` before auth has even resolved.
 *
 * A day still honours the CLA-1148 intent that "a profession added on the
 * server reaches clients without a release" — it just arrives within a day
 * rather than on the very next load.
 */
const VOCABULARY_TTL_MS = 24 * 60 * 60 * 1000

let current: PractitionerTypeOption[] = SEED_PRACTITIONER_TYPES

function readStored(): PractitionerTypeOption[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.length === 0) return null
    // Shape-check rather than trust: a stale or half-written entry would
    // otherwise render blank options, which is harder to diagnose than falling
    // back to the seed.
    if (!parsed.every((o) => o && typeof o.value === 'string' && typeof o.label === 'string')) {
      return null
    }
    return parsed as PractitionerTypeOption[]
  } catch {
    return null
  }
}

const stored = readStored()
if (stored) current = stored

/**
 * Adopt the served vocabulary. Called once after sign-in; see
 * `useVocabularyHydration` in `@/api/vocabulary-hooks`.
 */
export function hydratePractitionerTypes(options: PractitionerTypeOption[]): void {
  if (!options.length) return
  current = options
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(options))
    localStorage.setItem(STORAGE_AT_KEY, String(Date.now()))
  } catch {
    /* private mode — the in-memory copy still stands for this session */
  }
}

/**
 * Whether the served vocabulary needs fetching on this boot (CLA-1170).
 *
 * True unless a stored copy is actually in effect AND was fetched inside the
 * TTL. Deliberately checks `current !== SEED_PRACTITIONER_TYPES` rather than
 * the timestamp alone: a timestamp with no usable stored list (private mode,
 * a shape-check rejection) must not suppress the fetch and strand everyone on
 * the seed.
 */
export function needsVocabularyFetch(now: number = Date.now()): boolean {
  if (current === SEED_PRACTITIONER_TYPES) return true
  try {
    const at = Number(localStorage.getItem(STORAGE_AT_KEY))
    if (!Number.isFinite(at) || at <= 0) return true
    // A clock that has moved backwards would otherwise trust the copy
    // indefinitely, so treat any future timestamp as unusable.
    if (at > now) return true
    return now - at > VOCABULARY_TTL_MS
  } catch {
    return true
  }
}

/** Test seam: back to the built-in list, as if nothing had been fetched. */
export function resetPractitionerTypes(): void {
  current = SEED_PRACTITIONER_TYPES
  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(STORAGE_AT_KEY)
  } catch {
    /* ignore */
  }
}

/** The vocabulary in effect right now — served if it has landed, seed if not. */
export function practitionerTypes(): PractitionerTypeOption[] {
  return current
}

/** Grouped for a `<select>`, so the two workforces read as distinct. */
export function practitionerTypeGroupsBase(): { label: string; options: PractitionerTypeOption[] }[] {
  const by = (r: Registration) => current.filter((t) => t.registration === r)
  return [
    { label: 'Registered health practitioner', options: by('ahpra') },
    { label: 'Non-registered practitioner', options: by('none') },
    { label: 'Other', options: by('unknown') },
  ]
}

function byValue(): Map<string, PractitionerTypeOption> {
  return new Map(current.map((t) => [t.value, t]))
}

/**
 * The select's groups, with the shortlist an AHPRA registration number implies
 * pinned to the top (CLA-1147).
 *
 * `/auth/me` returns `practitioner_type_candidates`, derived server-side from
 * the board prefix on the user's own registration number — `NMW` narrows to the
 * four nursing and midwifery roles, `MED` to one. The full vocabulary stays
 * below it: this narrows the question, it does not answer it, and someone whose
 * registration says one thing and whose role says another must still be able to
 * say so.
 *
 * Unknown slugs are dropped rather than rendered. The server owns the
 * prefix map and this file mirrors the vocabulary, so a suggestion this
 * build has never heard of is a stale SPA — better to show one fewer
 * option than an option with no label.
 */
export const SUGGESTED_GROUP_LABEL = 'Suggested by your AHPRA registration'

export function practitionerTypeGroups(
  candidates?: string[] | null,
): { label: string; options: PractitionerTypeOption[] }[] {
  const suggested = (candidates ?? [])
    .map((c) => resolve(c))
    .filter((o): o is PractitionerTypeOption => Boolean(o))

  const base = practitionerTypeGroupsBase()
  if (suggested.length === 0) return base

  return [{ label: SUGGESTED_GROUP_LABEL, options: suggested }, ...base]
}

// Legacy spellings the backend normalises on write but which may still arrive
// on a read of an un-normalised row, or in a response cached before the split.
const ALIASES: Record<string, string> = {
  laser_technician: 'laser_therapist',
  laser_tech: 'laser_therapist',
  dermal_therapy: 'dermal_therapist',
  beautician: 'beauty_therapist',
  esthetician: 'aesthetician',
  medical_practitioner: 'doctor',
  gp: 'doctor',
  general_practitioner: 'doctor',
  rn: 'registered_nurse',
  en: 'enrolled_nurse',
  np: 'nurse_practitioner',
}

/** Slug (or alias, or legacy spelling) to its option, or undefined. */
function resolve(raw: string): PractitionerTypeOption | undefined {
  const slug = normalise(raw)
  const map = byValue()
  return map.get(slug) ?? map.get(ALIASES[slug] ?? '')
}

function normalise(raw: string): string {
  return raw.trim().toLowerCase().replace(/[\s-]+/g, '_')
}

/**
 * Human label for a stored slug.
 *
 * Falls back to title-casing the slug rather than rendering it raw: years of
 * unvalidated writes left free text and abbreviations in this column, and
 * `dermal_therapist` on screen reads as a bug. Returns `null` for an empty
 * value so callers can choose their own placeholder.
 */
export function practitionerTypeLabel(raw: string | null | undefined): string | null {
  if (!raw || !raw.trim()) return null
  const known = resolve(raw)
  if (known) return known.label
  const slug = normalise(raw)
  return slug
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
}

/**
 * The OTHER question — AHPRA registration status. Kept here so the two
 * vocabularies are visibly separate rather than quietly interchangeable.
 */
export const REGISTRATION_STATUSES = ['registered', 'non_registered', 'admin_only'] as const

export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number]

const REGISTRATION_LABELS: Record<string, string> = {
  registered: 'Registered',
  non_registered: 'Non-registered',
  admin_only: 'Admin',
}

export function registrationStatusLabel(raw: string | null | undefined): string | null {
  if (!raw || !raw.trim()) return null
  return REGISTRATION_LABELS[normalise(raw)] ?? raw
}
